import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Partners from "@/components/home/Partners";
import EngagedMarquee from "@/components/EngagedMarquee";
import FilmMark from "@/components/FilmMark";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

export default function PartnersPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/blog")
      .then((r) => setNews((r.data || []).filter((p) => (p.tags || []).some((t) => /partenariat/i.test(t)))))
      .catch(() => {});
  }, []);

  return (
    <main className="pt-24" data-testid="partners-page">
      <FilmMark />
      <EngagedMarquee />
      <Partners />

      {/* Actualités partenariats */}
      {news.length > 0 && (
        <section className="mt-20 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="partnership-news">
          <div className="meta-label text-cinabre">§ {en ? "Partnership news · Events & collaborations" : "Actualités partenariats · Événements & collaborations"}</div>
          <h2 className="mt-4 font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {en ? <>Our <em className="italic">partnerships</em> in motion.</> : <>Nos <em className="italic">partenariats</em> en marche.</>}
          </h2>
          <p className="mt-6 max-w-2xl text-fg/70 font-serif italic text-lg md:text-xl leading-snug">
            {en ? "Each collaboration writes a chapter of the Thread — a sport, a design, a cause, a season. Here are the upcoming events." : "Chaque collaboration écrit un chapitre du Fil — un sport, un design, une cause, une saison. Voici les rendez-vous à venir."}
          </p>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {news.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: (i % 4) * 0.06 }}
                data-testid={`partnership-item-${p.slug}`}
              >
                <Link to={`/carnet/${p.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden border border-fg/15 bg-fg/5">
                    {p.hero_image && (
                      <img src={p.hero_image} alt={p.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]" />
                    )}
                  </div>
                  <div className="mt-5">
                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">
                      § {en ? "Partnership" : "Partenariat"} · {formatDate(p.published_at)}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl md:text-3xl leading-tight tracking-tight group-hover:text-cinabre transition-colors">
                      {p.title}
                    </h3>
                    {p.subtitle && (
                      <p className="mt-3 font-serif italic text-base text-fg/70 leading-snug">
                        {p.subtitle}
                      </p>
                    )}
                    {p.excerpt && (
                      <p className="mt-3 text-fg/75 leading-relaxed text-sm">{p.excerpt}</p>
                    )}
                    <div className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre group-hover:gap-3 transition-all">
                      {en ? "Read the article" : "Lire l'article"} <ArrowRight size={13} />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>

          <div className="mt-14 border-t border-fg/10 pt-8 flex items-center gap-6 flex-wrap">
            <Link to="/carnet" className="btn-editorial ghost">
              {en ? "All the Thread's chronicles →" : "Toutes les chroniques du Fil →"}
            </Link>
            <p className="font-serif italic text-fg/70 max-w-md">
              {en ? <>A partnership to imagine together? <Link to="/contact" className="text-cinabre border-b border-cinabre/40 hover:border-cinabre">Write to us</Link>.</> : <>Un partenariat à imaginer ensemble ? <Link to="/contact" className="text-cinabre border-b border-cinabre/40 hover:border-cinabre">Écrivez-nous</Link>.</>}
            </p>
          </div>
        </section>
      )}
    </main>
  );
}
